<template>
    <div
        :id="`process-element-${element.id}`"
        :style="containerStyles"
        class="process-element-overlay"
        ref="processElementOverlay"
        v-if="currentEntityType !== EntityType.FLOW"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        @mousemove="handleMouseMove"
    >
        <!-- Always render the top level div, since it defines the width -->
        <Popup
            v-if="
                !isEditing &&
                currentEntityId &&
                currentEntityType &&
                isHoverPopupEnabled
            "
            :anchor="processElementOverlayElement!"
        >
            <GenericCard
                v-if="isHoverPopupEnabled"
                :entityId="currentEntityId"
                :entityType="currentEntityType"
                :showPath="false"
            />
        </Popup>
        <RouterLink
            :id="`process-element-link-${element.id}`"
            class="process-element-link"
            :class="{
                hover: isHovering,
            }"
            :to="routerLink"
            :aria-label="currentElement?.name ?? businessObject.name"
        />
    </div>
</template>

<script setup lang="ts">
import { type Element as BpmnElement } from "bpmn-js/lib/model/Types";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import { computed, ref, toRef, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import Popup from "@/components/Popup.vue";
import { useBpmnManager } from "@/composables/bpmn-manager";
import * as bpmn_helpers from "@/composables/bpmn-manager/helpers";
import { useEditManager } from "@/composables/edit-manager";
import { ProcessElementType } from "@/lib/openapi/gen";
import { EntityType } from "@/stores/entity-store";
import {
    ProcessElement,
    useProcessElementsManager,
} from "@/stores/process-elements-store";

const props = withDefaults(
    defineProps<{
        element: BpmnElement;
        bpmnManager: ReturnType<typeof useBpmnManager>;
        isHoverPopupEnabled?: boolean;
    }>(),
    {
        isHoverPopupEnabled: true,
    }
);

const element = toRef(props.element);
const { processElements } = useProcessElementsManager();
const { isEditing } = useEditManager();
const isHovering = ref(false);
const route = useRoute();
const processElementOverlayElement = useTemplateRef("processElementOverlay");
const businessObject = computed(() => getBusinessObject(element.value));

const containerStyles = computed(() => {
    // Add padding to the element container to overlay BPMN element border
    return {
        "width": element.value.width + 16 + "px",
        "height": element.value.height + 16 + "px",
    };
});

const currentEntityId = computed(() => {
    const bo = businessObject.value;
    return bo.processElementId || bo.processId || bo.calledElementId;
});

const currentEntityType = computed(() => {
    const bo = businessObject.value;
    if (bo.processElementId) {
        const type = bpmn_helpers.elementType(element.value);
        return type ? PROCESS_ELEMENT_ENTITY_TYPE_MAP[type] : undefined;
    } else if (bo.processId) {
        return EntityType.PROCESS;
    }
    return bo.calledElementType as EntityType | undefined;
});

/**
 * Get the current element from the processElements list.
 */
const currentElement = computed<ProcessElement | undefined>(() => {
    const processElementId = businessObject.value.processElementId;
    if (!processElementId) return;
    return processElements.value?.find((t) => t.identity === processElementId);
});

const PROCESS_ELEMENT_ENTITY_TYPE_MAP = {
    [ProcessElementType.TASK]: EntityType.TASK,
    [ProcessElementType.EVENT]: EntityType.EVENT,
    [ProcessElementType.GATEWAY]: EntityType.GATEWAY,
    [ProcessElementType.FLOW]: EntityType.FLOW,
};

const routerLink = computed(() => {
    const bo = businessObject.value;
    const commonParams = {
        orgName: route.params.orgName,
        stekName: route.params.stekName,
    };

    if (bo.processElementId) {
        return {
            name: "processElementPage",
            params: {
                ...commonParams,
                elementId: bo.processElementId,
                elementType: bpmn_helpers.elementType(element.value),
            },
        };
    }

    const type = bo.calledElementType as EntityType;
    const id = bo.calledElementId || bo.processId;

    if (type === EntityType.DOMAIN) {
        return {
            name: "domainPage",
            params: { ...commonParams, domainId: id },
        };
    } else if (type === EntityType.PRODUCT) {
        return {
            name: "productPage",
            params: { ...commonParams, productId: id },
        };
    } else if (
        type === EntityType.PROCESS ||
        bpmn_helpers.elementIsCallActivity(element.value)
    ) {
        return {
            name: "processPage",
            params: { ...commonParams, processId: id },
        };
    }

    return {};
});

const handleMouseMove = (event: MouseEvent) => {
    props.bpmnManager.eventBus?.fire("element.mousemove", {
        element: element.value,
        gfx: undefined,
        originalEvent: event,
        type: undefined,
    });
};

const handleMouseEnter = (event: MouseEvent) => {
    isHovering.value = true;
    props.bpmnManager.eventBus?.fire("element.hover", {
        element: element.value,
        gfx: undefined,
        originalEvent: event,
        type: undefined,
    });
};

const handleMouseLeave = (event: MouseEvent) => {
    isHovering.value = false;
    props.bpmnManager.eventBus?.fire("element.out", {
        element: element.value,
        gfx: undefined,
        originalEvent: event,
        type: undefined,
    });
};
</script>

<style scoped lang="scss">
.process-element-link {
    text-decoration: none;
    padding: 8px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    // Give the text same properties as the BPMN text.
    color: black;
    font-family: Arial, sans-serif;
    line-height: 1.2;
    font-size: 12px;

    &.hover {
        color: white;
    }

    * {
        max-width: 90%;
        text-wrap-mode: wrap;
        overflow-wrap: break-word;
    }
}
</style>
